import React, { useEffect, useRef, useState } from 'react';

const STAR_COUNT = 550;

const planets = [
  {
    id: 'venus',
    name: 'Venus',
    fact: "Venus spins backwards — its day is longer than its year.",
    className: 'planet-venus',
    style: { top: '20%', left: '8%' },
  },
  {
    id: 'jupiter',
    name: 'Jupiter',
    fact: "Jupiter is so massive, every other planet could fit inside it.",
    className: 'planet-jupiter',
    style: { top: '38%', right: '10%' },
  },
  {
    id: 'saturn',
    name: 'Saturn',
    fact: "Saturn is less dense than water — it would float in a big enough bathtub.",
    className: 'planet-saturn',
    style: { top: '58%', left: '6%' },
  },
];

// Bias star distribution toward the top of the page: squaring a [0,1) random
// value skews results toward 0, so the density gradually fades going down.
const makeStar = () => ({
  xPct: Math.random(),
  yPct: Math.pow(Math.random(), 2.6),
  size: 0.8 + Math.random() * 1.6,
  phase: Math.random() * Math.PI * 2,
  speed: 2 + Math.random() * 1.5,
});

const AstronomyNightSky = () => {
  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const starsRef = useRef(null);

  if (!starsRef.current) {
    starsRef.current = Array.from({ length: STAR_COUNT }, makeStar);
  }

  // All 550 stars twinkle on a single <canvas> with one requestAnimationFrame
  // loop, instead of 550 separate DOM nodes with individual CSS animations —
  // dramatically cheaper for the main thread and compositor at this count.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let rafId;
    let resizeTimeout;

    const resize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resize, 150);
    };
    window.addEventListener('resize', handleResize);

    const draw = (time) => {
      ctx.clearRect(0, 0, width, height);
      const t = time / 1000;
      ctx.fillStyle = '#FFFFFF';
      for (const s of starsRef.current) {
        const twinkle = Math.sin(t * s.speed + s.phase) * 0.5 + 0.5;
        ctx.globalAlpha = 0.15 + twinkle * 0.85;
        ctx.beginPath();
        ctx.arc(s.xPct * width, s.yPct * height, s.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <div className="astronomy-sky" ref={containerRef}>
      <canvas ref={canvasRef} className="astronomy-stars-canvas" />

      {planets.map((p) => (
        <div
          key={p.id}
          className={`astronomy-planet ${p.className}`}
          style={p.style}
          onMouseEnter={() => setHoveredPlanet(p.id)}
          onMouseLeave={() => setHoveredPlanet(null)}
          onClick={() => setHoveredPlanet((cur) => (cur === p.id ? null : p.id))}
        >
          {p.id === 'saturn' && <span className="saturn-ring" />}
          {hoveredPlanet === p.id && (
            <div className="planet-tooltip">
              <strong>{p.name}</strong>
              <span>{p.fact}</span>
            </div>
          )}
        </div>
      ))}

      <div
        className="astronomy-moon"
        onMouseEnter={() => setHoveredPlanet('moon')}
        onMouseLeave={() => setHoveredPlanet(null)}
        onClick={() => setHoveredPlanet((cur) => (cur === 'moon' ? null : 'moon'))}
      >
        <div className="astronomy-moon-halo" />
        {hoveredPlanet === 'moon' && (
          <div className="planet-tooltip moon-tooltip">
            <strong>The Moon</strong>
            <span>I captured this photo myself, through my own telescope.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AstronomyNightSky;
